import { Box, InlineStack, PlainButton, Tooltip } from '@customerio/pluma-components/react';
import React from 'react';
import * as styles from './index.css';
import { isEmptyString } from '@/utils/string';

export interface MessageContainerProps extends React.ComponentProps<typeof Box> {
  variant: 'user' | 'model';
}

export function MessageContainer({ variant, children, ...props }: MessageContainerProps) {
  return (
    <Box {...props} className={styles.messageContainerVariants[variant]}>
      <Box className={[styles.messageContainerContent, styles.messageContainerContentVariants[variant]]}>
        {children}
      </Box>
    </Box>
  );
}

export function MessageActionsContainer({
  isVisible = false,
  children,
  ...props
}: {
  isVisible?: boolean;
  children: React.ReactNode;
} & React.ComponentProps<typeof InlineStack>) {
  return (
    <InlineStack
      gap="025"
      alignBlock="center"
      {...props}
      className={[styles.messageActionsContainer, isVisible && styles.visibleMessageActionsContainer]}
    >
      {children}
    </InlineStack>
  );
}

interface MessageActionProps extends Omit<React.ComponentProps<typeof PlainButton>, 'children' | 'ref'> {
  variant?: 'default' | 'active';
  tooltip?: string;
  children: React.ReactNode;
}

export function MessageAction({
  variant = 'default',
  tooltip,
  className = '',
  children,
  ref: forwardedRef,
  ...props
}: MessageActionProps & { ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement> }) {
  // Floating UI accepts standard DOM props, while Pluma's style prop also permits null.
  const { style, ...referenceProps } = props;

  return (
    <Tooltip content={tooltip} placement="top" isCustom={true} isDisabled={isEmptyString(tooltip)}>
      {(context) => (
        <PlainButton
          className={[styles.messageAction, styles.messageActionVariants[variant], className]}
          ref={(node) => {
            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else if (forwardedRef != null) {
              forwardedRef.current = node;
            }
            context.reference.setReference(node);
          }}
          {...referenceProps}
          style={style}
          {...context.reference.getReferenceProps(referenceProps)}
        >
          {children}
        </PlainButton>
      )}
    </Tooltip>
  );
}
